import styles from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/TaskViewerSheet.module.css";
import ViewerFragment, {
  ViewerFragmentCommonProps,
} from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import UserNameViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/UserNameViewerFragment/UserNameViewerFragment";
import UserAndTeamViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/UserAndTeamViewerFragment/UserAndTeamViewerFragment";
import {
  FileItem,
  Files,
} from "@coworker/apprestructured/src/task-management/components/Files/Files";
import {
  DEFAULT_TAB,
  LogsAndComments,
  Tab,
} from "@coworker/apprestructured/src/task-management/components/LogsAndComments/LogsAndComments";
import { memo, ReactNode, useMemo, useState } from "react";
import { useTranslation } from "@coworker/locales";
import ViewerFragmentConfig from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragmentConfig";
import { useTaskLogs } from "@coworker/apprestructured/src/task-management/services/useTaskLogs";
import { useTaskComments } from "@coworker/apprestructured/src/task-management/services/useTaskComments";
import { useTaskCommentsImages } from "@coworker/apprestructured/src/task-management/services/useTaskCommentsImages";
import { Log } from "@coworker/apprestructured/src/task-management/types/Log";
import { timestampToTimeDiff } from "@coworker/apprestructured/src/task-management/util";
import { CommentsTabItem } from "@coworker/apprestructured/src/task-management/components/LogsAndComments/CommentsTab/CommentsTab";
import { Comment } from "@coworker/apprestructured/src/task-management/types/Comment";
import { LOADING } from "@coworker/apprestructured/src/task-management/loading";

const NO_COMMENT_IMAGE_URLS: string[] = [];

interface TaskViewerSheetContentProps {
  taskId: string;
  headerElement: ReactNode;
  imagesElement: ReactNode;
  viewerFragments: ViewerFragmentConfig[];
  viewerFragmentCommonProps: ViewerFragmentCommonProps;
  startElement: ReactNode;
  fileItems: FileItem[];
  addCommentText: string;
  addCommentImages: File[];
  isCreatingComment: boolean;
  addCommentImageUploaderIsReady: boolean;
  onAddCommentTextChange: (text: string) => void;
  setAddCommentImages: (images: File[]) => void;
  onAddCommentClick: () => void;
}

export const TaskViewerSheetContent = memo(
  (props: TaskViewerSheetContentProps) => {
    const { t } = useTranslation();
    const {
      taskId,
      headerElement,
      viewerFragmentCommonProps,
      viewerFragments,
      imagesElement,
      startElement,
      fileItems,
      addCommentText,
      addCommentImages,
      isCreatingComment,
      addCommentImageUploaderIsReady,
      onAddCommentTextChange,
      setAddCommentImages,
      onAddCommentClick,
    } = props;
    // Note that this just reflects the currently active tab, not control it
    const [historyAndCommentsTab, setHistoryAndCommentsTab] =
      useState<Tab>(DEFAULT_TAB);

    const { data: logs, isLoading: isLoadingLogs } = useTaskLogs(taskId);
    const { data: comments, isLoading: isLoadingComments } =
      useTaskComments(taskId);
    const { data: commentImages, isLoading: isLoadingCommentsImages } =
      useTaskCommentsImages(
        historyAndCommentsTab === "comments" ? taskId : null
      );

    const logsTabItems = useMemo(
      () =>
        logs?.map((log: Log) => {
          const params = log.params?.map((param) => param) || [];
          const translationParams =
            log.translation_params.map((param) => t(param)) || [];

          return {
            title: log.title,
            content: t(log.translation_key, {
              params: params,
              translationParams: translationParams,
            }),
            time: timestampToTimeDiff({
              dateTime: log.created_at,
              t,
            }),
          };
        }) || [],
      [logs, t]
    );

    const commentsTabItems = useMemo<CommentsTabItem[]>(
      () =>
        comments?.map((comment: Comment) => {
          const commentImageUrls = () => {
            if (comment.image_ids.length === 0) {
              return NO_COMMENT_IMAGE_URLS;
            } else if (commentImages === undefined) {
              return LOADING;
            } else {
              const imageUrls = commentImages
                .filter(({ id }) => comment.image_ids.includes(id))
                .map(({ signed_url }) => signed_url);
              if (imageUrls.length !== comment.image_ids.length) {
                // We have created a new comment and are currently refetching images
                return LOADING;
              } else {
                return imageUrls;
              }
            }
          };

          const commentImageCreateUploads =
            commentImages
              ?.filter(({ id }) => comment.image_ids.includes(id))
              .map(({ name, size, bucket_id }) => ({
                name,
                size: Number(size),
                bucket_id,
              })) ?? [];

          return {
            id: comment.id,
            title: comment.title,
            content: comment.text,
            time: timestampToTimeDiff({
              dateTime: comment.created_at,
              t,
            }),
            imageUrls: commentImageUrls(),
            imageCreateUploads: commentImageCreateUploads,
            creatorUserId: comment.creator_user_id,
          };
        }) || [],
      [comments, t, commentImages]
    );

    return (
      <>
        {headerElement}
        <div className={styles["contentWrapper"]}>
          {imagesElement}
          {viewerFragments.map((config, i) => (
            <ViewerFragment
              key={i}
              config={config}
              commonProps={viewerFragmentCommonProps}
            />
          ))}
          <UserNameViewerFragment
            configProps={{
              title: t("createdByString"),
              field: "creator_user_id",
            }}
            commonProps={viewerFragmentCommonProps}
          />
          <UserAndTeamViewerFragment
            configProps={{
              title: t("assignedToTaskFilterString"),
              userIdField: "assigned_user_id",
              teamIdField: "assigned_team_id",
            }}
            commonProps={viewerFragmentCommonProps}
          />
          {startElement}
          <Files items={fileItems} />
          <LogsAndComments
            taskId={taskId}
            logsTabItems={logsTabItems}
            commentsTabItems={commentsTabItems}
            isLoadingLogs={isLoadingLogs}
            isLoadingComments={isLoadingComments || isLoadingCommentsImages}
            onTabChanged={setHistoryAndCommentsTab}
            addCommentText={addCommentText}
            addCommentImages={addCommentImages}
            disableAllAddCommentInputs={isCreatingComment}
            disableAddCommentButton={!addCommentImageUploaderIsReady}
            onAddCommentTextChange={onAddCommentTextChange}
            onAddCommentImagesChange={setAddCommentImages}
            onAddCommentClick={onAddCommentClick}
          />
        </div>
      </>
    );
  }
);
